import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable, forkJoin } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { switchMap, map, tap, catchError, take } from 'rxjs/operators';
import { AppNotificationService } from '../../shared/services/notification.service';
import { AttachmentService, IAttachmentResponse, IUploadAttachmentRequest, IUploadAttachmentResponse } from '../../shared/services/attachment.service';
import { DeviceServiceService } from '../../devices/services/service.service';
import { IGetDeviceByIdForRevision } from '../../devices/models/device'
import { IAddServiceAttachment } from '../../devices/models/service-attachment';
import { ServiceAttachmentService } from '../../devices/services/service-attachment.service';
import { DeviceService } from '../../devices/services/device.service';
import { DeviceStore } from './device-store';

export interface AttachmentState {
  attachments: IAttachmentResponse[];
  images: IAttachmentResponse[];
  documents: IAttachmentResponse[];
  isLoading: boolean;
}

@Injectable()
export class AttachmentStore extends ComponentStore<AttachmentState> {

  // Initial state
  constructor(
    private attachmentService: AttachmentService,
    private deviceServiceService: DeviceServiceService,
    private deviceStore: DeviceStore,
    private serviceAttachmentService: ServiceAttachmentService,
    private appnotificationService: AppNotificationService) {
      super({ 
        attachments: [], 
        images: [],
        documents: [],
        isLoading: false
      });


      // Initialize derived state
      this.setDerivedState();
  }

  // Selector for the list of attachments
  readonly attachments$: Observable<IAttachmentResponse[]> = this.select(state => state.attachments);
  
  readonly images$: Observable<IAttachmentResponse[]> = this.select(
    this.attachments$,
    attachments => attachments.filter(attachment => attachment.fileExtension.startsWith('image/'))
  );

  readonly documents$: Observable<IAttachmentResponse[]> = this.select(
    this.attachments$,
    attachments => attachments.filter(attachment => attachment.fileExtension.startsWith('application/pdf'))
  );

  readonly isLoading$: Observable<boolean> = this.select(state => state.isLoading);

  // Effect to load attachments by device
  readonly getAttachmentsByDevice = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      switchMap(deviceId =>
        this.attachmentService.getAttachmentsByDevice({deviceId}).pipe(
          tap(attachments => {
            const sortedAttachments = attachments.data.sort();

            this.patchState(state => ({
              attachments: [...state.attachments, ...sortedAttachments],
              isLoading: false
            }));
          })
        )
      ),
      take(1)
    )
  );

  // Effect to load attachments by device
  readonly getAttachmentsByRevision = this.effect<{ revisionId: string; attachmentId: string }>(input$ =>
    input$.pipe(
      switchMap(({ revisionId, attachmentId }) =>
        this.attachmentService.getAttachmentsByRevision({ revisionId }).pipe(
          tap(attachments => {
            // Filter out the specified attachmentId
            const filteredAttachments = attachments.data.filter(
              attachment => attachment.id !== attachmentId
            );

            // Sort the remaining attachments
            const sortedAttachments = filteredAttachments.sort();

            // Update the state with the filtered and sorted attachments
            this.patchState(state => ({
              attachments: [...state.attachments, ...sortedAttachments],
              isLoading: false
            }));
          })
        )
      ),
      take(1)
    )
  );


  // Effect to load attachments by installation
  readonly getAttachmentsByInstallation = this.effect<string>(installationId$ =>
    installationId$.pipe(
      switchMap(installationId => 
        this.attachmentService.getAttachmentsByInstallation({installationId: installationId}).pipe(
          tap(filteredAttachments => { 
            this.patchState({ attachments: filteredAttachments.data, isLoading: false })
          })
        )
      )
    )
  );

    // Effect to load attachments by service
    readonly getAttachmentsByService = this.effect<string>(serviceId$ =>
      serviceId$.pipe(
        switchMap(serviceId => 
          this.attachmentService.getAttachmentsByService({serviceId: serviceId}).pipe(
            tap(filteredAttachments => { 
              this.patchState({ attachments: filteredAttachments.data, isLoading: false })
            })
          )
        )
      )
    );

  // Method to add a attachment
  readonly addAttachment = this.effect<FormData>(attachment$ =>
    attachment$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      switchMap(attachment =>
        this.attachmentService.upload(attachment).pipe(
          tap(newAttachment => {
            // Append the new attachment to the existing array
            this.patchState(state => ({
              attachments: [...state.attachments, newAttachment].sort(),
              isLoading: false
            }));

            if (newAttachment) {
              if(newAttachment.serviceId !== null) {

                var request: IAddServiceAttachment = {
                  serviceId: newAttachment.serviceId,
                  attachmentId: newAttachment.id,
                  showInReport: false
                }

                this.serviceAttachmentService.addServiceAttachment(request).subscribe(() => {
                  this.deviceServiceService.getServiceById(newAttachment.serviceId).subscribe((service) => {
                    var request: IGetDeviceByIdForRevision = {
                      deviceId: service.deviceId,
                      revisionId: service.revisionId
                    }
                    this.deviceStore.updateDeviceById({request});
                  })
                })
              }
            }
            
            this.appnotificationService.notification$.next({ message:translate('attachment.createSuccess'), style: 'success' });
          })
        )
      )
    )
  );

  // Method to add multiple attachments
  readonly addAttachments = this.effect<FormData[]>(attachments$ =>
    attachments$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      switchMap(attachments =>
        forkJoin(attachments.map(attachment => this.attachmentService.upload(attachment))).pipe(
          tap(newAttachments => {
            // Append the new attachments to the existing array and sort
            this.patchState(state => ({
              attachments: [...state.attachments, ...newAttachments].sort(),
              isLoading: false
            }));

            // Process each new attachment
            newAttachments.forEach(newAttachment => {
              if (newAttachment?.serviceId !== null) {
                const request: IAddServiceAttachment = {
                  serviceId: newAttachment.serviceId,
                  attachmentId: newAttachment.id,
                  showInReport: false
                };

                this.serviceAttachmentService.addServiceAttachment(request);
              }
            });

            // Show notification
            this.appnotificationService.notification$.next({
              message: translate('attachment.createSuccess'),
              style: 'success'
            });
          })
        )
      )
    )
  );

  // Method to remove a attachment from store
  readonly removeAttachmentById = this.effect<string>(attachmentId$ =>
    attachmentId$.pipe(
      switchMap(attachmentId =>
        this.attachmentService.delete(attachmentId).pipe(
          tap(() => {
            this.patchState(state => ({
              attachments: state.attachments.filter(attachment => attachment.id !== attachmentId),
              isLoading: false
            }));
            this.appnotificationService.notification$.next({ message:translate('attachment.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message:translate('attachment.deleteFailed'), style: 'error' });
            return EMPTY;
          })
        )
      )
    )
  );
/*
  readonly download = this.effect<string>(attachmentId$ =>
    attachmentId$.pipe(
      switchMap(attachmentId => 
        this.attachmentService.download({ attachmentId }).pipe(
          tap(blob => {
            const mimeType = blob.type;
            
            // Convert blob to base64 data if needed
            const reader = new FileReader();
            reader.onloadend = () => {
              const data = reader.result as string; // base64 or blob URL
              
              this.patchState(state => ({
                attachments: state.attachments.map(attachment =>
                  attachment.id === attachmentId
                    ? { ...attachment, data, mimeType }
                    : attachment
                )
              }));
  
              this.appnotificationService.notification$.next({ message: translate('attachment.downloadSuccess'), style: 'success' });
            };
            reader.readAsDataURL(blob);  // Reads blob as base64 URL
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message: translate('attachment.downloadFailed'), style: 'error' });
            return EMPTY;
          })
        )
      )
    )
  );
  
*/
  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );

  // Method to initialize derived state selectors
  private setDerivedState() {
    this.select(state => state.attachments).subscribe(attachments => {
      const images = attachments.filter(att => att.fileExtension.startsWith('image/'));
      const documents = attachments.filter(att => !att.fileExtension.startsWith('application/pdf'));

      this.patchState({
        images,
        documents
      });
    });
  }
}
